import { isOwOfferFill, isSnipeProtectedTrade } from '@/utils/trade-shield'
import { useMemo } from 'react'
import { REMOTE_SLUGS } from '@/utils/constants'
import useCollectionInscriptions from '@/utils/hooks/useCollectionInscriptions'
import { useSalesFeed, type FeedRow } from '@/lib/sales-feed/hooks'

export interface UnifiedCollectionSale {
	source: 'ow' | 'global'
	inscription_id: string
	row_key: string
	txid: string
	timestamp_ms: number
	marketplace: number
	price_sats: number
	amount?: number
	seller_address?: string | null
	buyer_address?: string | null
	inscriptions_in_tx: number
	is_mempool_pending?: boolean
	name?: string
	/** Settled through snipe protection (Ordinals Wallet rows only). */
	protected?: boolean
	/** An accepted bid (offer) rather than a listing purchase. */
	via_bid?: boolean
}

/** A feed row in the shape the activity panel renders. */
export function toUnifiedSale(row: FeedRow): UnifiedCollectionSale {
	const amount = row.amount == null ? undefined : Number(row.amount)
	return {
		source: row.source === 'ow' ? 'ow' : 'global',
		inscription_id: row.inscription_id ?? '',
		row_key: row.key,
		txid: row.txid ?? '',
		timestamp_ms: row.ts,
		marketplace: typeof row.marketplace === 'number' ? row.marketplace : 0,
		price_sats: Number(row.price_sats) || 0,
		amount: Number.isFinite(amount) ? amount : undefined,
		seller_address: row.seller_address ?? null,
		buyer_address: row.buyer_address ?? null,
		inscriptions_in_tx: row.inscriptions_in_tx ?? 1,
		is_mempool_pending: row.status === 'pending',
		name: row.name ?? undefined,
		protected: isSnipeProtectedTrade(row),
		via_bid: isOwOfferFill(row)
	}
}

/**
 * A collection's sales across every venue, pending included, as one live
 * list. The API merges and orders the sources and pushes changes; see
 * `@/lib/sales-feed`.
 */
export default function useCollectionSalesActivity(slug: string) {
	const isRemoteCollection = REMOTE_SLUGS.includes(slug)
	const feed = useSalesFeed(slug)

	const { data: inscriptions } = useCollectionInscriptions(slug, !isRemoteCollection)
	const inscriptionsMap = useMemo(() => {
		const map: Record<string, any> = {}
		if (!isRemoteCollection && inscriptions) {
			for (const e of inscriptions as any[]) {
				if (e?.id) map[e.id] = e
			}
		}
		return map
	}, [inscriptions, isRemoteCollection])

	const rows = useMemo(() => feed.rows.map(toUnifiedSale), [feed.rows])

	return {
		rows,
		visibleRows: rows,
		freshKeys: feed.fresh,
		inscriptionsMap,
		isRemoteCollection,
		fetchMore: feed.fetchMore,
		hasMore: feed.hasMore,
		isLoading: feed.isLoading && rows.length === 0,
		isLoadingMore: feed.isLoadingMore,
		error: rows.length === 0 ? feed.error : null,
		live: feed.transport === 'live',
		watermark: { canPageMore: feed.hasMore }
	}
}
